import { useEffect, useMemo, useState } from "react";
import {
  ISSUE_SEVERITIES,
  ISSUE_SEVERITY_META,
  createIssueSchema,
  describeBreadcrumb,
  collapseBreadcrumbs,
  type Issue,
  type IssueSeverity,
} from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { clearTrail, trail } from "../lib/breadcrumbs.js";
import { capturarTela, coletarAmbiente, NOSHOT_ATTR, type Captura } from "../lib/screenshot.js";
import { ErrorBox } from "./ui.js";

// =====================================================================
// Botão "Reportar erro" + formulário do chamado.
//
// O formulário pede TRÊS coisas e mais nada: o que aconteceu, o que era
// para acontecer, e o quanto atrapalha. Todo o resto — passos, tela,
// navegador, rota, versão, erros de API — o portal já sabe e anexa
// sozinho. Cada campo a mais é uma chance de a pessoa desistir no meio e
// o defeito continuar acontecendo em silêncio.
// =====================================================================

/** Prefill vindo de quem abriu o relato (ex.: a tela de erro fatal). */
export interface PrefillRelato {
  title?: string;
  description?: string;
  severity?: IssueSeverity;
}

const EVENTO_ABRIR = "bn:reportar-erro";

/**
 * Abre o relato de qualquer lugar do código, sem contexto nem props.
 *
 * Um CustomEvent em vez de um contexto React de propósito: quem mais
 * precisa disparar o relato é o ErrorBoundary — e um boundary que já
 * capturou uma exceção não pode depender da árvore de providers que
 * acabou de quebrar.
 */
export function abrirRelatoDeErro(prefill?: PrefillRelato): void {
  window.dispatchEvent(new CustomEvent<PrefillRelato>(EVENTO_ABRIR, { detail: prefill ?? {} }));
}

// ---------------------------------------------------------------------
// Botão flutuante
// ---------------------------------------------------------------------

export function ReportarErro({ oculto = false }: { oculto?: boolean }) {
  const [captura, setCaptura] = useState<Captura | null>(null);
  const [aberto, setAberto] = useState(false);
  const [capturando, setCapturando] = useState(false);
  const [prefill, setPrefill] = useState<PrefillRelato>({});
  // Marca discreta de que algo falhou desde o último relato — convida sem
  // interromper. Um toast a cada 500 viraria ruído e seria fechado no reflexo.
  const [houveFalha, setHouveFalha] = useState(false);

  const iniciar = async (p: PrefillRelato = {}) => {
    if (capturando || aberto) return;
    setPrefill(p);
    setCapturando(true);
    // A captura acontece ANTES do modal existir: fotografar depois
    // registraria o formulário de relato por cima do problema.
    const c = await capturarTela();
    setCaptura(c);
    setCapturando(false);
    setAberto(true);
  };

  useEffect(() => {
    const abrir = (ev: Event) => void iniciar((ev as CustomEvent<PrefillRelato>).detail ?? {});
    const falhou = () => setHouveFalha(true);
    window.addEventListener(EVENTO_ABRIR, abrir);
    window.addEventListener("bn:api-erro", falhou);
    return () => {
      window.removeEventListener(EVENTO_ABRIR, abrir);
      window.removeEventListener("bn:api-erro", falhou);
    };
    // `iniciar` fecha sobre estado que só serve de guarda de reentrância;
    // registrar uma vez basta e evita re-assinar o evento a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* `oculto` monta só o OUVINTE, sem o botão flutuante. É como a tela
          de erro fatal reaproveita este formulário: ela já tem o próprio
          botão grande, e um flutuante ao lado seria uma segunda porta para
          a mesma sala. */}
      {!oculto && (
      <button
        {...{ [NOSHOT_ATTR]: "" }}
        data-testid="botao-reportar-erro"
        aria-label={capturando ? "Capturando tela" : "Reportar erro"}
        className="fixed bottom-6 right-24 z-40 flex h-14 items-center gap-2 rounded-full bg-white px-5 text-sm font-semibold text-slate-700 shadow-lg ring-1 ring-brand-100 transition hover:bg-red-50 hover:text-red-700 hover:ring-red-200 disabled:opacity-70"
        onClick={() => void iniciar()}
        disabled={capturando}
        title="Reportar um erro nesta tela (captura a tela e os passos automaticamente)"
      >
        {capturando ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
        ) : (
          <img src="/icons/suporte.png" alt="" width={32} height={32} className="h-8 w-8 shrink-0 object-contain" />
        )}
        <span className="hidden sm:inline">{capturando ? "Capturando…" : "Reportar erro"}</span>
        {houveFalha && !aberto && (
          <span className="absolute -right-1 -top-1 flex h-3 w-3" title="Houve uma falha nesta sessão">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
          </span>
        )}
      </button>
      )}

      {aberto && (
        <FormularioRelato
          captura={captura}
          prefill={prefill}
          onClose={() => {
            setAberto(false);
            setHouveFalha(false);
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------
// Formulário
// ---------------------------------------------------------------------

function FormularioRelato({
  captura,
  prefill,
  onClose,
}: {
  captura: Captura | null;
  prefill: PrefillRelato;
  onClose: () => void;
}) {
  // `window.location` e não `useLocation()`: este formulário também é aberto
  // pela tela de erro fatal, que renderiza FORA do BrowserRouter (o router
  // pode ser parte do que quebrou). Um hook de rota ali lançaria uma segunda
  // exceção — dentro do mecanismo que existe para relatar a primeira.
  const { pathname, search } = window.location;
  const [title, setTitle] = useState(prefill.title ?? "");
  const [description, setDescription] = useState(prefill.description ?? "");
  const [expected, setExpected] = useState("");
  const [severity, setSeverity] = useState<IssueSeverity>(prefill.severity ?? "atrapalha");
  const [anexarCaptura, setAnexarCaptura] = useState(true);
  const [verDetalhes, setVerDetalhes] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [criado, setCriado] = useState<Issue | null>(null);

  // Congelada na montagem: se a trilha continuasse viva, os cliques DENTRO
  // do formulário entrariam no relato e empurrariam para fora os passos que
  // levaram ao erro.
  const passos = useMemo(() => collapseBreadcrumbs(trail()), []);
  const ambiente = useMemo(() => coletarAmbiente(), []);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  async function enviar() {
    setErro(null);
    const payload = {
      title: title.trim(),
      description: description.trim(),
      expected: expected.trim(),
      severity,
      route: `${pathname}${search}`,
      breadcrumbs: trail(),
      client: ambiente,
      screenshot: anexarCaptura && captura ? captura.dataUrl : undefined,
    };
    // Valida com o MESMO schema do servidor: a pessoa vê o problema no
    // campo em vez de receber um 400 depois de escrever tudo.
    const parsed = createIssueSchema.safeParse(payload);
    if (!parsed.success) {
      setErro(new Error(parsed.error.issues.map((i) => i.message).join(" · ")));
      return;
    }
    setEnviando(true);
    try {
      const { data } = await api.post("/issues", parsed.data);
      setCriado(data as Issue);
      // A trilha cumpriu o papel: zerar evita que os passos deste chamado
      // reapareçam colados no próximo, contando uma história misturada.
      clearTrail();
    } catch (e) {
      setErro(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      {...{ [NOSHOT_ATTR]: "" }}
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 pt-10"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Reportar erro"
      >
        <div className="brand-stripe h-1.5 w-full" />

        {criado ? (
          <Confirmacao issue={criado} onClose={onClose} />
        ) : (
          <div className="p-6">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Reportar erro</h2>
                <p className="mt-1 text-sm text-slate-500">
                  A tela e os seus últimos passos já foram capturados. Só falta contar o que aconteceu.
                </p>
              </div>
              <button className="text-slate-400 hover:text-slate-600" onClick={onClose} aria-label="Fechar">
                ✕
              </button>
            </div>

            {erro != null && (
              <div className="mb-4">
                <ErrorBox error={erro} />
              </div>
            )}

            <div className="grid gap-5 md:grid-cols-[1fr_18rem]">
              <div className="space-y-4">
                <div>
                  <label className="label">O que aconteceu?</label>
                  <input
                    className="input"
                    data-testid="relato-titulo"
                    placeholder="Ex.: a grade da Torre abriu vazia para USIMINAS"
                    value={title}
                    maxLength={160}
                    onChange={(e) => setTitle(e.target.value)}
                    autoFocus
                  />
                  <p className="mt-1 text-xs text-slate-400">
                    Uma frase. É o título que o time vai ler primeiro. {title.length}/160
                  </p>
                </div>

                <div>
                  <label className="label">Detalhes (opcional)</label>
                  <textarea
                    className="input min-h-[5rem]"
                    data-testid="relato-descricao"
                    placeholder="Qualquer coisa que ajude: a que horas, com qual filtro, se já aconteceu antes…"
                    value={description}
                    maxLength={4000}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div>
                  <label className="label">O que era para ter acontecido? (opcional)</label>
                  <textarea
                    className="input min-h-[3.5rem]"
                    data-testid="relato-esperado"
                    placeholder="Ex.: deveria listar as 30 cargas da competência"
                    value={expected}
                    maxLength={2000}
                    onChange={(e) => setExpected(e.target.value)}
                  />
                </div>

                <fieldset>
                  <legend className="label">O quanto isso atrapalha?</legend>
                  <div className="space-y-2">
                    {ISSUE_SEVERITIES.map((s) => {
                      const m = ISSUE_SEVERITY_META[s];
                      const ativo = severity === s;
                      return (
                        <label
                          key={s}
                          data-testid={`relato-severidade-${s}`}
                          className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 transition ${
                            ativo ? "border-brand-500 bg-brand-50 ring-1 ring-brand-200" : "border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name="severidade"
                            className="mt-1"
                            checked={ativo}
                            onChange={() => setSeverity(s)}
                          />
                          <span>
                            <span className="block text-sm font-medium text-slate-800">{m.label}</span>
                            <span className="block text-xs text-slate-500">{m.pergunta}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              </div>

              {/* Coluna do que vai anexado — visível de propósito. Um relato
                  que envia captura de tela sem mostrar o que está na captura
                  é uma surpresa desagradável esperando para acontecer. */}
              <aside className="space-y-3">
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Captura</span>
                    {captura && (
                      <label className="flex items-center gap-1.5 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          data-testid="relato-anexar-captura"
                          checked={anexarCaptura}
                          onChange={(e) => setAnexarCaptura(e.target.checked)}
                        />
                        anexar
                      </label>
                    )}
                  </div>
                  {captura ? (
                    <>
                      <img
                        src={captura.dataUrl}
                        alt="Captura da tela no momento do erro"
                        data-testid="relato-previa"
                        className={`w-full rounded-lg border border-slate-200 transition ${anexarCaptura ? "" : "opacity-30 grayscale"}`}
                      />
                      <p className="mt-1.5 text-[11px] text-slate-400">
                        {captura.largura}×{captura.altura} · {Math.round(captura.bytes / 1024)} KB
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-slate-400">
                      Não foi possível capturar a tela desta vez. O relato segue valendo — os passos vão junto.
                    </p>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 p-3">
                  <button
                    className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700"
                    onClick={() => setVerDetalhes((v) => !v)}
                    data-testid="relato-ver-passos"
                  >
                    <span>{passos.length} passos anexados</span>
                    <span>{verDetalhes ? "▴" : "▾"}</span>
                  </button>
                  {verDetalhes && (
                    <ol className="mt-2 max-h-56 space-y-1 overflow-y-auto text-[11px] leading-relaxed text-slate-600">
                      {passos.length === 0 && <li className="text-slate-400">Nenhum passo registrado ainda.</li>}
                      {passos.map((b, i) => (
                        <li key={i} className={b.ok === false ? "font-medium text-red-600" : ""}>
                          {describeBreadcrumb(b)}
                        </li>
                      ))}
                    </ol>
                  )}
                  <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
                    Vão junto: tela, navegador e erros do sistema.{" "}
                    <strong className="font-medium text-slate-500">O que você digitou nos campos não é gravado.</strong>
                  </p>
                </div>
              </aside>
            </div>

            <div className="mt-6 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
              <span className="text-xs text-slate-400">
                Tela <code className="rounded bg-slate-100 px-1">{pathname}</code> · versão {ambiente.appVersion}
              </span>
              <div className="flex gap-2">
                <button className="btn-ghost" onClick={onClose}>
                  Cancelar
                </button>
                <button
                  className="btn-primary"
                  data-testid="relato-enviar"
                  onClick={() => void enviar()}
                  disabled={enviando || title.trim().length < 8}
                >
                  {enviando ? "Enviando…" : "Enviar chamado"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Confirmação
// ---------------------------------------------------------------------

function Confirmacao({ issue, onClose }: { issue: Issue; onClose: () => void }) {
  const m = ISSUE_SEVERITY_META[issue.severity];
  return (
    <div className="p-8 text-center" data-testid="relato-confirmacao">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-3xl">✓</div>
      <h2 className="text-lg font-semibold text-slate-900">Chamado registrado</h2>
      {/* O código aparece grande porque é o que a pessoa vai citar depois
          ("e aquele BN-42?"). Sem um identificador, "abri um chamado" não é
          rastreável nem por quem abriu nem por quem tria. */}
      <p className="mt-3 font-mono text-2xl font-bold tracking-tight text-brand-700" data-testid="relato-codigo">
        {issue.code}
      </p>
      <p className="mx-auto mt-3 max-w-md text-sm text-slate-500">
        Classificado como <strong>{m.prioridade}</strong> ({m.label.toLowerCase()}). O time olha chamados desta
        prioridade em <strong>{m.sla}</strong>.
      </p>
      <p className="mx-auto mt-2 max-w-md text-xs text-slate-400">
        Você acompanha o andamento em <strong>Chamados</strong>, no menu lateral.
      </p>
      <div className="mt-6 flex justify-center gap-2">
        <a className="btn-ghost" href={`/chamados?id=${issue.id}`}>
          Ver o chamado
        </a>
        <button className="btn-primary" onClick={onClose}>
          Voltar ao trabalho
        </button>
      </div>
    </div>
  );
}
